<template>
  <div class="flex flex-col gap-y-4">

  <BaseInlineNotification
    v-if="inlineResetError"
    heading-text="An Error Occurred"
    hide-title-element
    type="error"
    class="max-lg:px-4"
  >
    <p class="body-sm" v-text="inlineResetError" />
  </BaseInlineNotification>

  <BaseInlineNotification
    v-if="inlineResetSuccess"
    heading-text="Success"
    hide-title-element
    type="success"
    class="max-lg:px-4"
  >
    <p class="body-sm" v-text="inlineResetSuccess" />
  </BaseInlineNotification>

    <div class="flex items-center justify-between gap-4 max-lg:px-4 max-md:flex-col">
      <h1
        class="heading-md"
        v-text="'My Profile'"
      />

      <div class="flex items-center gap-x-2 max-md:w-full">
        <button
          v-if="editProfile"
          :disabled="isSubmitting"
          class="btn btn--primary max-md:flex-1"
          form="editProfileForm"
          type="submit"
        >
          <span class="relative inline-flex items-center justify-center">
            <BaseLoadingIcon :visible="isSubmitting" />
            <span
              class="whitespace-nowrap"
              :class="{ 'invisible': isSubmitting }"
              v-text="'Save Changes'"
            />
          </span>
        </button>
        <button
          v-else
          class="btn btn--primary max-md:flex-1"
          data-test-id="edit-profile-button"
          type="button"
          @click="editProfile = true"
          v-text="'Edit Profile'"
        />
      </div>
    </div>

    <EditProfileForm
      v-if="editProfile"
      :customer-id="user?.id as number"
      :licenses="licenses"
      :profile="customerProfile"
      @cancel-edit="editProfile = false"
      @license-deleted="handleLicenseDeleted"
      @save-changes="saveChanges"
      @update-is-submitting="isSubmitting = $event"
    />

    <ProfileDetails
      v-else
      :licenses="licenses"
      :profile="customerProfile"
    />
  </div>

  <BaseCard title="Change Password">
    <div>
      <form
        class="flex max-w-xl flex-col gap-y-6"
        novalidate
        @submit.prevent="handleChangePassword"
      >
        <BaseInlineNotification
          v-if="submissionError"
          heading-text="An Error Occurred"
          hide-title-element
          type="error"
        >
          <p class="body-sm" v-text="submissionError" />
        </BaseInlineNotification>

        <BaseInlineNotification
          v-if="submissionSuccess"
          heading-text="Password successfully changed"
          hide-title-element
          type="success"
        >
          <p class="body-sm" v-text="submissionSuccess" />
        </BaseInlineNotification>

        <BasePasswordInput
          id="currentPassword"
          v-model="values.currentPassword"
          :error-message="errors.currentPassword"
          label="Current Password"
          name="currentPassword"
          required
          validation-rules="required"
        />

        <BasePasswordInput
          id="password"
          v-model="values.password"
          :error-message="errors.password"
          label="New Password"
          name="password"
          required
          validation-rules="required|password"
        />

        <BasePasswordValidation
          :error="errors.password || ''"
          :password="values.password || ''"
          show-validation
        />

        <BaseButton
          :disabled="isPasswordSubmitting"
          class="btn--primary md:self-start"
          text="Change Password"
          type="submit"
        />
      </form>
    </div>
  </BaseCard>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue'
import { useForm } from 'vee-validate'
import { log } from '@js/utils/logger'
import { getCustomerProfile } from '@js/api/customer/getCustomerProfile'
import { getCustomerLicenses } from '@js/api/customer/getCustomerLicenses'
import { CustomerLicense } from '@js/api/types/customer/CustomerLicense'
import { CustomerProfile } from '@js/api/types/customer/CustomerProfile'
import { changePassword } from '@js/api/auth/changePassword'

import ProfileDetails from '@js/components/account/profile/ProfileDetails.vue'
import EditProfileForm from '@js/components/account/profile/EditProfileForm.vue'
import useAuth from '@js/composables/useAuth'
import useCart from '@js/composables/useCart'
import useRecaptcha from '@js/composables/useRecaptcha'

import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseCard from '../../global/BaseCard.vue'
import BasePasswordValidation from '../../global/forms/BasePasswordValidation.vue'
import BaseButton from '../../global/BaseButton.vue'
import BasePasswordInput from '../../global/forms/inputs/BasePasswordInput.vue'

defineOptions({
  name: 'AppProfile',
})

const {
  values,
  errors,
  resetForm,
  isSubmitting: isPasswordSubmitting,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    currentPassword: '',
    password: '',
  }
})

const { getAuthUser } = useAuth()
const { getCartFromMagento } = useCart()
const { getRecaptchaToken } = useRecaptcha()

const editProfile = ref(false)
const isSubmitting = ref(false)
const licenses = ref<CustomerLicense[]>([])
const customerProfile = ref<CustomerProfile | null>(null)
const submissionError = ref('')
const submissionSuccess = ref('')
const hasRecaptchaError = ref(false)

const user = computed(() => getAuthUser())
const inlineResetError = ref('')
const inlineResetSuccess = ref('')

const saveChanges = async (updatedProfile: CustomerProfile | null): Promise<void> => {
  editProfile.value = false

  customerProfile.value = updatedProfile

  await getCartFromMagento()
}

const handleLicenseDeleted = (licenseId: number): void => {
  licenses.value = licenses.value.filter(license => license.license_id !== licenseId)
}

const handleChangePassword = handleValidationSubmit(async () => {
  try {
    submissionError.value = ''
    hasRecaptchaError.value = false

    const token = await getRecaptchaToken('change_password')
    if (!token) {
      hasRecaptchaError.value = true
      return
    }

    await changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.password,
      recaptchaToken: token,
    })

    submissionError.value = ''
    submissionSuccess.value = 'Your password was successfully changed.'
    resetForm()
  } catch (error) {
    submissionSuccess.value = ''

    if (
      error === 'Invalid login or password.'
    ) {
      submissionError.value = 'Invalid login or password.'
    } else {
      submissionError.value = 'An error occurred. Please try again.'
    }

    log({
      message: 'Change Password Error',
      file: 'src/wcei/js/components/account/profile/AppProfile.vue',
    })
  }
})

onBeforeMount(async () => {
  try {
    const [profileData, licenseData] = await Promise.all([
      getCustomerProfile(user.value?.id as number),
      getCustomerLicenses(user.value?.id as number),
    ])

    customerProfile.value = profileData
    licenses.value = licenseData
  } catch (error) {
    log({
      message: 'Customer Profile or Licenses Retrieval Error',
      file: 'src/wcei/js/components/account/profile/AppProfile.vue',
    })
  }
})
</script>
